import {
  paymentSettingContentToForm,
  paymentSettingFormToContent,
} from '../payment/paymentSettingConstants';
import {
  buildCountryOptionKey,
  findCountryByOptionKey,
  formatAreaCode,
  formatCountryAdminLabel,
  resolveEnabledCountryKeys,
} from '@formosa/shared/utils/countryUtils';

export const FRONT_CONFIG_TABS = [
  { key: 'language', label: '語系' },
  { key: 'register', label: '登入/註冊' },
  { key: 'kycPayment', label: '存款/取款' },
  { key: 'verifyCode', label: '驗證碼' },
  { key: 'logo', label: 'logo' },
  { key: 'timeZone', label: '時區' },
  { key: 'global', label: '其他配置' },
];

export const REGISTER_CONFIG_KEY = 'register';
export const KYC_CONFIG_KEY = 'kyc';
export const VERIFY_CODE_CONFIG_KEY = 'verifyCode';
export const LOGO_CONFIG_KEY = 'logo';
export const TIME_ZONE_CONFIG_KEY = 'timeZone';
export const GLOBAL_CONFIG_KEY = 'global';

export const DATE_FORMAT_OPTIONS = [
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd' },
  { value: 'dd-MM-yyyy', label: 'dd-MM-yyyy' },
];

export const TIME_FORMAT_OPTIONS = [
  { value: '12h', label: '12小時制' },
  { value: '24h', label: '24小時制' },
];

export const GLOBAL_WALLET_TYPE_OPTIONS = [
  { value: 'CUSTOM1CURRENCY', label: '自定義幣種' },
  { value: 'STAKING', label: '鎖倉挖礦' },
  { value: 'LOCKED', label: '鎖倉金額' },
  { value: 'WITHDRAW', label: '提款統計' },
];

export const ENGLISH_LANG_CODE = 'en_US';

/** KycConfig.usedList 欄位（對齊後端 optionList value） */
export const KYC_FIELD_OPTIONS = [
  { value: 'country', label: '國家/地區' },
  { value: 'image', label: '上傳證件照片' },
  { value: 'certType', label: '證件類型' },
  { value: 'identityNumber', label: '證件號碼' },
  { value: 'birthday', label: '生日' },
  { value: 'gender', label: '性別' },
  { value: 'realName', label: '名字' },
  { value: 'lastName', label: '姓氏' },
];

/** VerifyCodeConfig 常數（對齊後端） */
export const VERIFY_CODE_KEYS = {
  REGISTER: 'register',
  TRANSACTION_PASSWORD: 'transactionPassword',
  FIRST_TRANSACTION_PASSWORD: 'firstTransactionPassword',
  LOGIN_PASSWORD: 'loginPassword',
  FORGET_PASSWORD: 'forgetPassword',
  UNBIND_ADDRESS: 'unbindAddress',
  NEED_OLD_TRANSACTION_PASSWORD: 'needOldTransactionPassword',
  NEED_OLD_LOGIN_PASSWORD: 'needOldLoginPassword',
};

export const VERIFY_CODE_OPTIONS = [
  { key: VERIFY_CODE_KEYS.REGISTER, label: '註冊', mode: 'binary' },
  { key: VERIFY_CODE_KEYS.FIRST_TRANSACTION_PASSWORD, label: '首次設置交易密碼', mode: 'binary' },
  { key: VERIFY_CODE_KEYS.FORGET_PASSWORD, label: '忘記密碼', mode: 'binary' },
  { key: VERIFY_CODE_KEYS.UNBIND_ADDRESS, label: '解綁錢包地址', mode: 'binary' },
  { key: VERIFY_CODE_KEYS.TRANSACTION_PASSWORD, label: '再次修改交易密碼', mode: 'passwordChange' },
  { key: VERIFY_CODE_KEYS.LOGIN_PASSWORD, label: '修改登入密碼', mode: 'passwordChange' },
];

export const TEST_MEMBER_WITHDRAW_OPTIONS = [
  { value: 'open_auto', label: '開放提現、提款自動審核' },
  { value: 'open_manual', label: '開放提現、提款不自動審核' },
  { value: 'closed', label: '不開放提現' },
];

function toBoolean(value, defaultValue = false) {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  return Boolean(value);
}

function toInputString(value) {
  if (value == null || value === '') {
    return '';
  }
  return String(value);
}

export function getLanguageLabel(language) {
  return language?.langNameCh || language?.langName || language?.langCode || '';
}

export function buildLanguageItems(languages) {
  return (languages || []).map((item) => ({
    key: item.langCode,
    label: getLanguageLabel(item),
    raw: item,
  }));
}

/** @deprecated 使用 formatCountryAdminLabel */
export function getCountryLabel(country) {
  return formatCountryAdminLabel(country);
}

/** country 使用 enName::areaCode 作為穿梭框 key */
export function buildCountryItems(countries) {
  return (countries || [])
    .filter((item) => item?.enName)
    .map((item) => ({
      key: buildCountryOptionKey(item),
      label: formatCountryAdminLabel(item),
      raw: item,
    }));
}

export { resolveEnabledCountryKeys };

export function resolveEnabledLanguageKeys(registerContent, languages) {
  const configured = Array.isArray(registerContent?.languages)
    ? registerContent.languages.filter(Boolean)
    : [];
  if (configured.length > 0) {
    return configured;
  }
  return (languages || [])
    .filter((item) => item.status === 1)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((item) => item.langCode);
}

export function registerContentToForm(content, countries) {
  const source = content || {};
  return {
    showOnlyAuthPage: toBoolean(source.showOnlyAuthPage, false),
    showLanguageSwitchUI: toBoolean(source.showLanguageSwitchUI, true),
    bindTouristAccountOnRegister: toBoolean(source.bindTouristAccountOnRegister, false),
    emailLogin: toBoolean(source.emailLogin, true),
    phoneLogin: toBoolean(source.phoneLogin, true),
    forgotPassword: toBoolean(source.forgotPassword, true),
    emailRegister: toBoolean(source.emailRegister, true),
    phoneRegister: toBoolean(source.phoneRegister, true),
    needPromotionCode: toBoolean(source.needPromotionCode, false),
    registerTradePassword: toBoolean(source.registerTradePassword, false),
    enabledCountryKeys: resolveEnabledCountryKeys(countries),
    tourists: toBoolean(source.tourists, false),
    sendBonusOnRegister: toBoolean(source.sendBonusOnRegister, false),
    sendBonusWithRegister: toInputString(source.sendBonusWithRegister ?? '0'),
  };
}

export function registerFormToContent(form) {
  return {
    showOnlyAuthPage: form.showOnlyAuthPage,
    showLanguageSwitchUI: form.showLanguageSwitchUI,
    bindTouristAccountOnRegister: form.bindTouristAccountOnRegister,
    emailLogin: form.emailLogin,
    phoneLogin: form.phoneLogin,
    forgotPassword: form.forgotPassword,
    emailRegister: form.emailRegister,
    phoneRegister: form.phoneRegister,
    needPromotionCode: form.needPromotionCode,
    registerTradePassword: form.registerTradePassword,
    enNames: form.enNames || [],
    tourists: form.tourists,
    sendBonusOnRegister: form.sendBonusOnRegister,
    sendBonusWithRegister: form.sendBonusWithRegister === '' ? 0 : Number(form.sendBonusWithRegister) || 0,
    verifyCode: form.verifyCode,
    languages: form.languages,
  };
}

export function kycContentToForm(content) {
  const source = content || {};
  const usedList = Array.isArray(source.usedList) && source.usedList.length > 0
    ? source.usedList
    : ['country', 'image'];
  let testMemberWithdraw = 'closed';
  if (toBoolean(source.enableTestMemberWithdrawal, false)) {
    testMemberWithdraw = toBoolean(source.autoApproveTestMemberWithdrawal, false)
      ? 'open_auto'
      : 'open_manual';
  }
  return {
    usedList,
    withdrawNeedKyc: toBoolean(source.withdrawNeedKyc, true),
    bindWithdrawAddressNeedKyc: toBoolean(source.bindWithdrawAddressNeedKyc, false),
    blockWithdrawWhenPending: !toBoolean(source.withdrawPending, true),
    unbindAddressNotAllowed: toBoolean(source.unbindAddressNotAllowed, true),
    withdrawalThresholdSwitch: toBoolean(source.withdrawalThresholdSwitch, false),
    minWithdrawal: toInputString(source.minWithdrawal ?? '0'),
    minWithdrawalBalancePercent: toInputString(source.minWithdrawalBalancePercent ?? '0'),
    depositAutoLock: toBoolean(source.depositAutoLock, false),
    testMemberWithdraw,
    autoApproveTestMemberDeposit: toBoolean(source.autoApproveTestMemberDeposit, false),
    checkFirstWithdrawWalletRange: toBoolean(source.checkFirstWithdrawWalletRange, false),
    firstWithdrawWalletRangeConditions: Array.isArray(source.firstWithdrawWalletRangeConditions)
      ? source.firstWithdrawWalletRangeConditions.map((row) => ({
          minBalance: toInputString(row?.minBalance ?? ''),
          maxBalance: toInputString(row?.maxBalance ?? ''),
          totalDepositAmount: toInputString(row?.totalDepositAmount ?? ''),
          selected: toBoolean(row?.selected, true),
        }))
      : [],
  };
}

export function kycPaymentContentToForm(kycContent, paymentContent) {
  return {
    ...kycContentToForm(kycContent),
    payment: paymentSettingContentToForm(paymentContent),
  };
}

export function kycFormToContent(form) {
  const testMemberWithdraw = form.testMemberWithdraw || 'closed';
  return {
    usedList: form.usedList || [],
    withdrawNeedKyc: form.withdrawNeedKyc,
    bindWithdrawAddressNeedKyc: form.bindWithdrawAddressNeedKyc,
    withdrawPending: !form.blockWithdrawWhenPending,
    unbindAddressNotAllowed: form.unbindAddressNotAllowed,
    withdrawalThresholdSwitch: form.withdrawalThresholdSwitch,
    minWithdrawal: form.minWithdrawal === '' ? 0 : Number(form.minWithdrawal) || 0,
    minWithdrawalBalancePercent: form.minWithdrawalBalancePercent === ''
      ? 0
      : Number(form.minWithdrawalBalancePercent) || 0,
    depositAutoLock: form.depositAutoLock,
    enableTestMemberWithdrawal: testMemberWithdraw !== 'closed',
    autoApproveTestMemberWithdrawal: testMemberWithdraw === 'open_auto',
    autoApproveTestMemberDeposit: form.autoApproveTestMemberDeposit,
    checkFirstWithdrawWalletRange: form.checkFirstWithdrawWalletRange,
    firstWithdrawWalletRangeConditions: (form.firstWithdrawWalletRangeConditions || []).map((row) => ({
      minBalance: row.minBalance === '' ? 0 : Number(row.minBalance) || 0,
      maxBalance: row.maxBalance === '' ? 0 : Number(row.maxBalance) || 0,
      totalDepositAmount: row.totalDepositAmount === '' ? 0 : Number(row.totalDepositAmount) || 0,
      selected: row.selected !== false,
    })),
  };
}

export function parseVerifyCodeMode(usedList, key) {
  const list = Array.isArray(usedList) ? usedList : [];
  if (list.includes(key)) {
    return 'enabled';
  }
  if (key === VERIFY_CODE_KEYS.TRANSACTION_PASSWORD
    && list.includes(VERIFY_CODE_KEYS.NEED_OLD_TRANSACTION_PASSWORD)) {
    return 'need_old';
  }
  if (key === VERIFY_CODE_KEYS.LOGIN_PASSWORD
    && list.includes(VERIFY_CODE_KEYS.NEED_OLD_LOGIN_PASSWORD)) {
    return 'need_old';
  }
  if (key === VERIFY_CODE_KEYS.TRANSACTION_PASSWORD
    || key === VERIFY_CODE_KEYS.LOGIN_PASSWORD) {
    return 'direct';
  }
  return 'disabled';
}

export function verifyCodeContentToForm(content) {
  const usedList = Array.isArray(content?.usedList) ? [...content.usedList] : [];
  const modes = {};
  VERIFY_CODE_OPTIONS.forEach((option) => {
    modes[option.key] = parseVerifyCodeMode(usedList, option.key);
  });
  return { modes };
}

export function verifyCodeFormToContent(form) {
  const usedList = [];
  VERIFY_CODE_OPTIONS.forEach((option) => {
    const mode = form.modes?.[option.key] || 'disabled';
    if (mode === 'enabled') {
      usedList.push(option.key);
      return;
    }
    if (option.mode !== 'passwordChange') {
      return;
    }
    if (mode === 'need_old') {
      usedList.push(
        option.key === VERIFY_CODE_KEYS.TRANSACTION_PASSWORD
          ? VERIFY_CODE_KEYS.NEED_OLD_TRANSACTION_PASSWORD
          : VERIFY_CODE_KEYS.NEED_OLD_LOGIN_PASSWORD
      );
    }
  });
  return { usedList };
}

export function logoContentToForm(content) {
  const source = content || {};
  return {
    tenantName: source.tenantName || '',
    icon: source.icon || '',
    banner: source.banner || '',
    email: source.email || '',
    backendSiteName: source.backendSiteName || '',
    backendSiteIcon: source.backendSiteIcon || '',
  };
}

export function logoFormToContent(form) {
  return {
    tenantName: form.tenantName?.trim() || '',
    icon: form.icon || '',
    banner: form.banner || '',
    email: form.email || '',
    backendSiteName: form.backendSiteName?.trim() || '',
    backendSiteIcon: form.backendSiteIcon || '',
  };
}

export function timeZoneContentToForm(content) {
  const source = content || {};
  return {
    timeZone: source.timeZone || 'Z',
    useBrowserTimezone: toBoolean(source.useBrowserTimezone, true),
    dateFormat: source.dateFormat || 'yyyy-MM-dd',
    timeFormat: source.timeFormat || '12h',
  };
}

export function timeZoneFormToContent(form) {
  return {
    useBrowserTimezone: form.useBrowserTimezone,
    dateFormat: form.dateFormat,
    timeFormat: form.timeFormat,
  };
}

export function formatTimeFormatLabel(value) {
  return TIME_FORMAT_OPTIONS.find((item) => item.value === value)?.label || value || '—';
}

export function formatDateFormatLabel(value) {
  return DATE_FORMAT_OPTIONS.find((item) => item.value === value)?.label || value || '—';
}

function parseDomainLines(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatDomainLines(domains) {
  if (!Array.isArray(domains) || domains.length === 0) {
    return '';
  }
  return domains.join('\n');
}

export function globalContentToForm(content) {
  const source = content || {};
  return {
    h5Url: source.h5Url || '',
    showBettingOdds: !toBoolean(source.hideBettingOdds, false),
    showDetailUpDownSign: toBoolean(source.showDetailUpDownSign, true),
    showPositionDetailDateAndPrice: toBoolean(source.showPositionDetailDateAndPrice, true),
    betAmountSwitch: toBoolean(source.betAmountSwitch, false),
    betAmount: toInputString(source.betAmount ?? '30000.00'),
    showCountryFlag: toBoolean(source.showCountryFlag, true),
    enableContractCancel: toBoolean(source.enableContractCancel, true),
    deliveriesNumber: toInputString(source.deliveriesNumber ?? ''),
    deliveriesAmountProportion: toInputString(source.deliveriesAmountProportion ?? ''),
    allowManualAmountInput: toBoolean(source.allowManualAmountInput, true),
    checkUsernameRegisterDomainEnable: toBoolean(source.checkUsernameRegisterDomainEnable, true),
    checkUsernameRegisterDomains: formatDomainLines(source.checkUsernameRegisterDomains),
    globalWalletTypes: Array.isArray(source.globalWalletTypes) ? [...source.globalWalletTypes] : [],
    showActivityIcon: toBoolean(source.showActivityIcon, false),
    followOrderLockedBalanceRestrictOpen: toBoolean(source.followOrderLockedBalanceRestrictOpen, false),
    followOrderLockedBalanceRestrictAmount: toInputString(
      source.followOrderLockedBalanceRestrictAmount ?? '1000.00'
    ),
  };
}

export function globalFormToContent(form) {
  const deliveriesNumber = form.deliveriesNumber === ''
    ? null
    : Number.parseInt(form.deliveriesNumber, 10);
  const deliveriesAmountProportion = form.deliveriesAmountProportion === ''
    ? null
    : Number(form.deliveriesAmountProportion);
  return {
    h5Url: form.h5Url?.trim() || '',
    hideBettingOdds: !form.showBettingOdds,
    showDetailUpDownSign: form.showDetailUpDownSign,
    showPositionDetailDateAndPrice: form.showPositionDetailDateAndPrice,
    betAmountSwitch: form.betAmountSwitch,
    betAmount: form.betAmount === '' ? null : Number(form.betAmount) || 0,
    showCountryFlag: form.showCountryFlag,
    enableContractCancel: form.enableContractCancel,
    deliveriesNumber: Number.isNaN(deliveriesNumber) ? null : deliveriesNumber,
    deliveriesAmountProportion: Number.isNaN(deliveriesAmountProportion)
      ? null
      : deliveriesAmountProportion,
    allowManualAmountInput: form.allowManualAmountInput,
    checkUsernameRegisterDomainEnable: form.checkUsernameRegisterDomainEnable,
    checkUsernameRegisterDomains: parseDomainLines(form.checkUsernameRegisterDomains),
    globalWalletTypes: form.globalWalletTypes || [],
    showActivityIcon: form.showActivityIcon,
    followOrderLockedBalanceRestrictOpen: form.followOrderLockedBalanceRestrictOpen,
    followOrderLockedBalanceRestrictAmount: form.followOrderLockedBalanceRestrictAmount === ''
      ? null
      : Number(form.followOrderLockedBalanceRestrictAmount) || 0,
  };
}

export function formatGlobalWalletTypeLabels(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return '—';
  }
  return values
    .map((value) => GLOBAL_WALLET_TYPE_OPTIONS.find((item) => item.value === value)?.label || value)
    .join('、');
}

export function formatBoolLabel(value, onText = '開', offText = '關') {
  return value ? onText : offText;
}

export function formatYesNo(value) {
  return value ? '是' : '否';
}

export function formatVerifyCodeModeLabel(mode) {
  if (mode === 'enabled') {
    return '啟用';
  }
  if (mode === 'need_old') {
    return '停用，需輸入舊密碼';
  }
  if (mode === 'direct') {
    return '停用，直接輸入新密碼';
  }
  return '停用';
}

export function splitKycPaymentContent(kycContent, paymentContent) {
  return kycPaymentContentToForm(kycContent, paymentContent);
}

export function buildCountrySavePayload(enabledKeys, allCountries) {
  const keys = Array.isArray(enabledKeys) ? enabledKeys : [];
  const rows = Array.isArray(allCountries) ? allCountries : [];
  const enNameOrder = [];
  const seenEnNames = new Set();

  keys.forEach((key) => {
    const country = findCountryByOptionKey(rows, key);
    if (country?.enName && !seenEnNames.has(country.enName)) {
      seenEnNames.add(country.enName);
      enNameOrder.push(country.enName);
    }
  });

  const enNameMap = new Map();
  rows.forEach((country) => {
    if (country?.enName && !enNameMap.has(country.enName)) {
      enNameMap.set(country.enName, country);
    }
  });

  const updates = [];
  enNameMap.forEach((country, enName) => {
    const enabled = keys.some((key) => findCountryByOptionKey(rows, key)?.enName === enName);
    const sortIndex = enNameOrder.indexOf(enName);
    updates.push({
      enName,
      delFlag: enabled ? 0 : 1,
      sort: sortIndex >= 0 ? sortIndex + 1 : (country.sort ?? 999),
    });
  });

  return { enNames: enNameOrder, updates };
}

export async function buildLanguageSavePayload(enabledKeys, allLanguages) {
  const keys = [...(enabledKeys || [])];
  if (!keys.includes(ENGLISH_LANG_CODE)) {
    keys.push(ENGLISH_LANG_CODE);
  }
  const enabledSet = new Set(keys);
  const updates = (allLanguages || []).map((lang) => {
    const enabled = enabledSet.has(lang.langCode);
    const sortIndex = keys.indexOf(lang.langCode);
    return {
      id: lang.id,
      langCode: lang.langCode,
      status: enabled ? 1 : 0,
      sort: enabled && sortIndex >= 0 ? sortIndex + 1 : lang.sort ?? 999,
    };
  });
  return { languages: keys, updates };
}

export { paymentSettingFormToContent };
